/**
 * Instant tooltip (no browser delay). Renders content in a portal, positions near trigger.
 * Use for hover labels and agent-tile stats instead of native title attribute.
 */
import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  /** Optional: position 'top' | 'bottom' | 'left' | 'right' */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** When true, wrapper is block-level full width (sidebar rows). Does not set height — avoids flex layout stretching menu items. */
  fillContainer?: boolean;
}

export function Tooltip({ content, children, placement = 'top', fillContainer = false }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    let top = 0;
    // For top/bottom: center tooltip on trigger using translateX(-50%) so alignment isn't pushed by clamping
    const centerX = rect.left + rect.width / 2;
    let left = centerX;
    switch (placement) {
      case 'bottom':
        top = rect.bottom + gap;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
      case 'top':
      default:
        top = rect.top - gap;
        break;
    }
    setPos({ top, left });
  }, [placement]);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => setVisible(false), []);

  const child = (
    <div
      ref={triggerRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      style={
        fillContainer
          ? { width: '100%', minWidth: 0, display: 'block', boxSizing: 'border-box' }
          : { display: 'inline-flex' }
      }
    >
      {children}
    </div>
  );

  const isTop = placement === 'top';
  const isBottom = placement === 'bottom';
  const isLeft = placement === 'left';
  const isRight = placement === 'right';
  const transform =
    isTop ? 'translate(-50%, -100%)' :
    isBottom ? 'translateX(-50%)' :
    isLeft ? 'translate(-100%, -50%)' :
    isRight ? 'translateY(-50%)' : undefined;
  const tooltipEl = visible && (
    <div
      role="tooltip"
      onMouseEnter={show}
      onMouseLeave={hide}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform,
        zIndex: 10000,
        padding: '6px 10px',
        fontSize: '0.8125rem',
        background: 'var(--widget-bg)',
        color: 'var(--text)',
        border: '1px solid var(--header-border, rgba(255,255,255,0.12))',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
        maxWidth: '280px',
        whiteSpace: 'normal',
      }}
    >
      {content}
    </div>
  );

  return (
    <>
      {child}
      {typeof document !== 'undefined' && createPortal(tooltipEl, document.body)}
    </>
  );
}
